/**
 * Display formatting.
 *
 * Money is paise (integers) and quantity is milli-units (integers) everywhere in this
 * codebase, matching the database. Nothing here ever does float arithmetic on money:
 * `rupees()` only converts for presentation.
 */

import { currentLocale } from '../i18n';

const RUPEE = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const RUPEE_COMPACT = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** `12800 -> "₹128.00"`. Indian digit grouping, because that is what the shop reads. */
export function rupees(paise: number): string {
  return RUPEE.format(paise / 100);
}

/** For dashboards where the paise are noise: `12800 -> "₹128"`. */
export function rupeesCompact(paise: number): string {
  return RUPEE_COMPACT.format(Math.round(paise / 100));
}

/** Bare number with no symbol, for table cells that carry a "Rate" column header. */
export function amount(paise: number): string {
  return (paise / 100).toFixed(2);
}

/** `1425 -> "1.425"`. Trims trailing zeros so 1000 reads as "1", not "1.000". */
export function quantity(milli: number): string {
  const value = milli / 1000;
  return Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, '');
}

/** `1800 -> "18"`, `2850 -> "28.5"`. GST slabs are quoted in percent, stored in basis points. */
export function percentFromBps(bps: number): string {
  const value = bps / 100;
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

/** "PC", "KG", "LTR" ... shown next to a quantity. */
export const UNIT_LABELS: Record<string, string> = {
  PC: 'pcs',
  KG: 'kg',
  G: 'g',
  LTR: 'L',
  ML: 'ml',
  PKT: 'pkt',
  BOX: 'box',
  BAG: 'bag',
  BTL: 'btl',
  CAN: 'can',
  DOZ: 'doz',
  ROLL: 'roll',
  MTR: 'm',
};

export function unitLabel(unit: string): string {
  return UNIT_LABELS[unit.toUpperCase()] ?? unit.toLowerCase();
}

/**
 * Every timestamp is rendered in IST. Without pinning the zone, Intl falls back to whatever the
 * browser happens to be set to, so a device in another zone shows a sale on a different day than
 * the report claims — and the two halves of the same screen disagree with each other.
 */
const IST = 'Asia/Kolkata';

/** `2026-01-31 -> "31 Jan"`. Short enough for an expiry column, unambiguous across quarters. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(currentLocale(), { day: '2-digit', month: 'short', timeZone: IST }).format(date);
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(currentLocale(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: IST,
  }).format(date);
}

export function timeOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(currentLocale(), { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: IST }).format(date);
}

/** Whole days from today until an expiry date. Negative means already expired. */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return Math.round((target - startOfToday.getTime()) / 86_400_000);
}

/** Masked mobile for the receipt footer and the customer list. */
export function maskPhone(phone: string): string {
  if (phone.length < 4) return phone;
  return `${'*'.repeat(Math.max(0, phone.length - 4))}${phone.slice(-4)}`;
}

const WARNING_TEXT: Record<string, (subject: string) => string> = {
  PRICE_OVERRIDE: (item) => `Price changed on the bill for ${item}.`,
  DISCOUNT_OVERRIDE: (item) => (item === 'bill' ? 'Bill discount is above the usual limit.' : `Discount on ${item} is above the usual limit.`),
  BATCH_OVERRIDE: (item) => `Batch for ${item} was chosen by hand, not by earliest expiry.`,
  SHORT_STOCK: (item) => `${item} sold with less stock on the shelf than the system showed.`,
  ABOVE_MRP: (item) => `${item} was sold above its MRP.`,
  DUPLICATE_SUBMISSION: () => 'This bill was already saved. Showing the original invoice.',
};

/** Server warnings arrive as `CODE:subject`. Turn them into a sentence a cashier can act on. */
export function humanizeWarning(raw: string): string {
  const [code = '', ...rest] = raw.split(':');
  const text = WARNING_TEXT[code];
  return text ? text(rest.join(':')) : raw;
}

/** `plural(1, "bill") -> "1 bill"`, `plural(3, "bill") -> "3 bills"`. */
export function plural(count: number, noun: string, many = `${noun}s`): string {
  return `${count} ${count === 1 ? noun : many}`;
}
